/**
 * The link a branch QR code for a `client_qr_completion` level resolves to.
 *
 * Shape:
 *   https://luqta.io/generate-qr/c/<contest uid>/u/<client>/l/<level uid>
 *                               /b/<branch uuid>/cn/<contest numeric id>
 *
 * Segments:
 *   c   contest UID          contest.uid
 *   u   client / company     level.client_uid, else the numeric client id
 *   l   level UID            level.uid
 *   b   branch               branch.branchId (uuid), with fallbacks
 *   cn  contest numeric id   contest.id
 *
 * Notes worth keeping in mind when changing this:
 *   - `c` and `cn` are the SAME contest: `c` is the public uuid, `cn` the
 *     numeric id the backend keys its records by.
 *   - `u` is NOT always a uuid — the builder prefers client_uid but falls back
 *     to the numeric client_id when no uuid exists, so whatever reads the link
 *     has to accept both forms.
 *   - `cn` is appended last and skipped when the id is missing, so its absence
 *     shortens the link rather than shifting the other segments.
 */

export const BRANCH_QR_BASE = 'https://luqta.io/generate-qr';

/** First value that is present and not blank, as a trimmed string. */
function firstValue(...values: unknown[]): string {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const text = String(value).trim();
    if (text && text !== '0') return text;
  }
  return '';
}

export interface BranchQrLinkParts {
  contestUid: string;
  clientRef: string;
  levelUid: string;
  branchRef: string;
  contestNumericId: string;
}

/** Pulls the five segment values out of the contest / level / branch records. */
export function readBranchQrLinkParts(
  contest: any,
  level: any,
  branch: any,
): BranchQrLinkParts {
  return {
    contestUid: firstValue(contest?.uid, contest?.contest_uid),
    // Prefer the uuid on the level; otherwise the client's numeric id, which
    // may sit on the level or on the contest's client record.
    clientRef: firstValue(
      level?.client_uid,
      level?.clientUid,
      level?.client_id,
      contest?.client?.id,
      contest?.client_id,
      contest?.clients_id,
      contest?.user_id,
    ),
    levelUid: firstValue(level?.uid, level?.level_uid),
    branchRef: firstValue(
      branch?.branchId,
      branch?.branch_id,
      branch?.client_branches_id,
      branch?.id,
    ),
    contestNumericId: firstValue(contest?.id, contest?.contest_id),
  };
}

/**
 * Builds the link, or '' when any required segment is missing — an incomplete
 * link would scan into a dead page, which is worse than offering none.
 * `cn` is the one optional segment.
 */
export function buildBranchQrLink(contest: any, level: any, branch: any): string {
  const parts = readBranchQrLinkParts(contest, level, branch);

  if (!parts.contestUid || !parts.clientRef || !parts.levelUid || !parts.branchRef) {
    return '';
  }

  let link =
    `${BRANCH_QR_BASE}` +
    `/c/${parts.contestUid}` +
    `/u/${parts.clientRef}` +
    `/l/${parts.levelUid}` +
    `/b/${parts.branchRef}`;

  if (parts.contestNumericId) {
    link += `/cn/${parts.contestNumericId}`;
  }

  return link;
}
